export enum SignUpStepKey {
  SignUpInfo = 'signUpInfo',
  BusinessInfo = 'businessInfo',
  PaymentUseCase = 'paymentUseCase',
  EcommercePlatform = 'ecommercePlatform',
  PaymentProvider = 'paymentProvider',
  POSPlatform = 'posPlatform',
  AnnualSales = 'annualSales',
  EligibilityCheck = 'eligibilityCheck',
  UserAccountCreation = 'userAccountCreation',
  VerifyEmail = 'verifyEmail',
}

export enum StepIndex {
  SignUpInfo = 0,
  BusinessInfo = 1,
  PaymentUseCase = 2,
  EcommercePlatform = 3,
  PaymentProvider = 4,
  POSPlatform = 5,
  AnnualSales = 6,
  EligibilityCheck = 7,
  UserAccountCreation = 8,
  VerifyEmail = 9,
}

export type SignUpStepConfig = {
  key: SignUpStepKey;
  label: string;
  title: string;
  description?: string;
};

export const signUpStepsConfig: SignUpStepConfig[] = [
  {
    key: SignUpStepKey.SignUpInfo,
    label: 'SignUp Info',
    title: 'Sign up to Zip',
  },
  {
    key: SignUpStepKey.BusinessInfo,
    label: 'Business Info',
    title: 'Welcome [First Name]! Tell us about your business',
  },
  {
    key: SignUpStepKey.PaymentUseCase,
    label: 'I will use Zip',
    title: 'What will you use Zip for?',
  },
  {
    key: SignUpStepKey.EcommercePlatform,
    label: 'ECommerce Platform',
    title: 'What is your e-commerce platform?',
  },
  {
    key: SignUpStepKey.PaymentProvider,
    label: 'Payment Provider',
    title: 'Who is your payment provider?',
    description:
      'Selecting your provider helps us tailor your onboarding experience.',
  },
  {
    key: SignUpStepKey.POSPlatform,
    label: 'POS Platform',
    title: 'What is your POS platform?',
    description:
      'Selecting your provider helps us tailor your onboarding experience.',
  },
  {
    key: SignUpStepKey.AnnualSales,
    label: 'Annual Sales',
    title: 'Total average annual sales',
    description:
      'This helps us understand what support you may need to integrate Zip.',
  },
  {
    key: SignUpStepKey.EligibilityCheck,
    label: 'Eligibility Check',
    title: 'Checking eligibility',
  },
  {
    key: SignUpStepKey.UserAccountCreation,
    label: 'User Account Creation',
    title: 'Almost there! Set your password',
  },
  {
    key: SignUpStepKey.VerifyEmail,
    label: 'Verify Email',
    title: 'Verify your email',
  },
];

export const getStepTitle = (key: SignUpStepKey): string | undefined => {
  return signUpStepsConfig.find((step) => step.key === key)?.title;
};

export const getStepLabel = (index: StepIndex): string => {
  return signUpStepsConfig[index].label;
};

export const getStepDescription = (key: SignUpStepKey): string | undefined => {
  return signUpStepsConfig.find((step) => step.key === key)?.description;
};

export const getStepKey = (index: StepIndex): SignUpStepKey => {
  return signUpStepsConfig[index].key;
};

/**
 * Returns the list of step keys to skip based on the selected paymentType.
 *
 * Online payments:              skip POSPlatform
 * Instore payments:             skip EcommercePlatform + PaymentProvider
 * Both (online and instore):    skip nothing
 */
export const getSkippedSteps = (
  paymentType: string | undefined
): SignUpStepKey[] => {
  if (paymentType === 'Online payments') {
    return [SignUpStepKey.POSPlatform];
  }
  if (paymentType === 'Instore payments') {
    return [SignUpStepKey.EcommercePlatform, SignUpStepKey.PaymentProvider];
  }
  // 'Both (online and instore)' or undefined — skip nothing
  return [];
};
